import { useMutation } from "@apollo/client";
import { AlertCircle, CalendarIcon } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { DeclineAppointmentDialog } from "@/domains/appointment/appointments/components/appointment-detail-dialog/components/decline-appointment-dialog";
import { useAppointmentViewContext } from "@/domains/appointment/appointments/shared/hooks/use-appointment-view-context";
import { useAppointments } from "@/domains/appointment/appointments/shared/hooks/use-appointments";
import { useUpdateAppointment } from "@/domains/appointment/appointments/shared/hooks/use-update-appointment";
import { useUpdateAppointmentServiceWorker } from "@/domains/appointment/appointments/shared/hooks/use-update-appointment-service-worker";
import { AppointmentBookingStatus, AppointmentOutcome } from "@/gql/graphql";
import { OWNER_CANCEL_APPOINTMENT_MUTATION } from "@/graphql/appointments";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { useDialogRouterSimple as useDialogRouter } from "@/lib/dialog/use-dialog-router-simple";
import { useSalonStore } from "@/lib/store/salon";
import { useNewCalendarUIStore } from "../../store/use-new-calendar-ui-store";
import { DesktopAppointmentTable } from "./desktop-appointment-table";
import { MobileAppointmentList } from "./mobile-appointment-list";

interface AppointmentListViewProps {
  currentDate: DateTime;
}

export function AppointmentListView({ currentDate }: AppointmentListViewProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation("newCalendar");
  const { currentSalon } = useSalonStore();

  // Decline dialog state
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [appointmentToDecline, setAppointmentToDecline] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Mutations
  const [ownerCancelAppointment] = useMutation(OWNER_CANCEL_APPOINTMENT_MUTATION);

  // Get multi-service expansion state
  const { multiServiceExpanded } = useNewCalendarUIStore();

  // Dialog management
  const { openDialog } = useDialogRouter();

  // Update appointment hooks
  const { updateAppointment } = useUpdateAppointment();
  const { updateAppointmentServiceWorker } = useUpdateAppointmentServiceWorker();

  // Use the same appointment hook as calendar modes
  const { appointments, loading, error } = useAppointments({
    currentDate: currentDate.toJSDate(), // Convert Luxon DateTime to Date
    viewType: "day", // Use day view for list mode to show daily appointments
  });

  // Register view context for AI assistant
  useAppointmentViewContext({
    key: "calendar",
    viewType: "List",
    viewIcon: "📋",
    currentDate,
    appointments,
  });

  // Event handlers
  const handleAppointmentClick = (appointmentId: string) => {
    openDialog({ type: "appointment-detail", id: appointmentId });
  };

  const handleStatusUpdate = async (
    appointmentId: string,
    newBookingStatus: AppointmentBookingStatus,
    newOutcome?: AppointmentOutcome
  ) => {
    await updateAppointment(appointmentId, {
      bookingStatus: newBookingStatus,
      outcome: newOutcome,
    });
  };

  // Handle decline confirm from dialog
  const handleDeclineConfirm = async (
    cancellationReasonType: string,
    cancellationReasonText: string
  ) => {
    if (!currentSalon || !appointmentToDecline) return;

    setIsUpdating(true);
    try {
      const result = await ownerCancelAppointment({
        variables: {
          salonId: currentSalon.id,
          appointmentId: appointmentToDecline,
          input: {
            cancellationReasonType: cancellationReasonType as any,
            cancellationReasonText: cancellationReasonText || null,
          },
        },
      });

      if (result.errors && result.errors.length > 0) {
        console.error("Failed to decline appointment:", result.errors);
        toast.error(result.errors[0].message || "Failed to decline appointment");
        return;
      }

      setShowDeclineDialog(false);
      setAppointmentToDecline(null);
      toast.success("Appointment declined successfully");
    } catch (error) {
      console.error("Failed to decline appointment:", error);
      toast.error("Failed to decline appointment");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOutcomeUpdate = async (appointmentId: string, newOutcome: string | null) => {
    try {
      await updateAppointment(appointmentId, { outcome: newOutcome as AppointmentOutcome | null });
    } catch (error) {
      console.error("Failed to update appointment outcome:", error);
    }
  };

  const handleServiceWorkerUpdate = async (
    appointmentId: string,
    serviceId: string,
    workerId: string | null
  ) => {
    try {
      await updateAppointmentServiceWorker(appointmentId, serviceId, workerId);
    } catch (error) {
      console.error("Failed to update service worker:", error);
    }
  };

  // Show loading only for initial load (hook now handles cached data properly)
  if (loading) {
    if (isMobile) {
      // Mobile skeleton - card layout
      return (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="mb-3 overflow-hidden">
              <CardContent className="p-4">
                {/* Top Row: Time + Service */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 rounded" />
                    <Skeleton className="h-5 w-12" />
                  </div>
                  <Skeleton className="h-4 w-24" />
                </div>

                {/* Bottom Row: Customer + Status */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1">
                    <Skeleton className="h-4 w-4 rounded" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-8 w-20 rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    // Desktop skeleton - table row layout
    return (
      <div className="rounded-md border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="h-12 px-4 text-left text-sm font-medium w-20">
                <Skeleton className="h-4 w-12" />
              </th>
              <th className="h-12 px-4 text-left text-sm font-medium w-48">
                <Skeleton className="h-4 w-32" />
              </th>
              <th className="h-12 px-4 text-left text-sm font-medium w-64">
                <Skeleton className="h-4 w-40" />
              </th>
              <th className="h-12 px-4 text-left text-sm font-medium w-40">
                <Skeleton className="h-4 w-28" />
              </th>
              <th className="h-12 px-4 text-left text-sm font-medium w-36">
                <Skeleton className="h-4 w-20" />
              </th>
            </tr>
          </thead>
          <tbody>
            {[...Array(3)].map((_, i) => (
              <tr key={i} className="border-b hover:bg-muted/50 h-12">
                {/* Time */}
                <td className="px-4 py-2">
                  <Skeleton className="h-4 w-12" />
                </td>
                {/* Customer */}
                <td className="px-4 py-2">
                  <Skeleton className="h-4 w-32" />
                </td>
                {/* Services */}
                <td className="px-4 py-2">
                  <Skeleton className="h-4 w-40" />
                </td>
                {/* Worker */}
                <td className="px-4 py-2">
                  <Skeleton className="h-4 w-24" />
                </td>
                {/* Status */}
                <td className="px-4 py-2">
                  <Skeleton className="h-8 w-20" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center gap-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <div className="text-center">
              <h3 className="font-semibold text-lg mb-2">{t("table.errorTitle")}</h3>
              <p className="text-muted-foreground mb-4">{t("table.errorDescription")}</p>
              <Button variant="outline" onClick={() => window.location.reload()}>
                {t("table.tryAgain")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (appointments.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold text-lg mb-2">{t("emptyState.noAppointments")}</h3>
            <p className="text-muted-foreground">{t("emptyState.noAppointmentsDescription")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render appropriate view based on device type
  return (
    <>
      {isMobile ? (
        <MobileAppointmentList
          appointments={appointments}
          currentDate={currentDate}
          onAppointmentClick={handleAppointmentClick}
          onStatusUpdate={handleStatusUpdate}
          onOutcomeUpdate={handleOutcomeUpdate}
          onServiceWorkerUpdate={handleServiceWorkerUpdate}
        />
      ) : (
        <DesktopAppointmentTable
          appointments={appointments}
          currentDate={currentDate}
          multiServiceExpanded={multiServiceExpanded}
          onAppointmentClick={handleAppointmentClick}
          onStatusUpdate={handleStatusUpdate}
          onOutcomeUpdate={handleOutcomeUpdate}
          onServiceWorkerUpdate={handleServiceWorkerUpdate}
        />
      )}

      {/* Decline Dialog */}
      <DeclineAppointmentDialog
        open={showDeclineDialog}
        onClose={() => {
          setShowDeclineDialog(false);
          setAppointmentToDecline(null);
        }}
        onConfirm={handleDeclineConfirm}
        isLoading={isUpdating}
      />
    </>
  );
}
